/* raw layout hash: _default-web-layout:cf6d116634d20a094209e5f23a98a526b4145bde162e7cb9a3ab8d84f18a2d74 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import NavMenu from './components/NavMenu.jsx';
import SideNav from './components/SideNav.jsx';
import BottomNav from './components/BottomNav.jsx';
import HelpDrawer from '../components/help/HelpDrawer.jsx';

const APP_NAME = 'TPA InsureOS';
const COMPACT_QUERY = '(max-width: 991.98px)';

// Shared application shell (page_layout: _default-web-layout). Fixed-top navbar
// with brand + the role-filtered navigation + the session/auth chrome; the page
// body is injected through <Outlet />.
//
// Unified responsive nav: the horizontal top nav is the desktop default; when
// the role-permitted items overflow the bar (or the viewport is compact) the
// navigation switches to a collapsible SIDE drawer opened from the hamburger.
// (A mobile bottom-nav layer is added separately.)
export default function DefaultWebLayout() {
  const { user, isAuthenticated, logout } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [navOverflow, setNavOverflow] = useState(false);
  const [isCompact, setIsCompact] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(COMPACT_QUERY).matches
      : false
  );

  const inlineNavRef = useRef(null);
  const togglerRef = useRef(null);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Track the compact (small-viewport) breakpoint.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(COMPACT_QUERY);
    const onChange = (e) => setIsCompact(e.matches);
    mql.addEventListener('change', onChange);
    setIsCompact(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Detect when the inline horizontal nav overflows its available space; when it
  // does, the drawer (hamburger) becomes the navigation. Re-measures on resize
  // and when the role (item count) changes.
  useEffect(() => {
    const el = inlineNavRef.current;
    if (!el) return undefined;

    const measure = () => {
      window.requestAnimationFrame(() => {
        if (!inlineNavRef.current) return;
        const node = inlineNavRef.current;
        setNavOverflow(node.scrollWidth > node.clientWidth + 4);
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [user, isCompact]);

  // The hamburger/drawer is the nav when the bar overflows or the viewport is compact.
  const useDrawer = navOverflow || isCompact;

  const showBottomNav = isAuthenticated && isCompact;

  return (
    <div className={`app-layout ${showBottomNav ? 'has-bottom-nav' : ''}`}>
      <nav className="navbar navbar-dark bg-primary text-light fixed-top">
        <div className="container-fluid d-flex align-items-center">
          {/* Hamburger → side drawer (shown when overflow / compact) */}
          {isAuthenticated && (
            <button
              ref={togglerRef}
              type="button"
              className={`btn btn-primary border-0 me-2 px-2 ${useDrawer ? '' : 'd-none'}`}
              onClick={openDrawer}
              aria-label="Open navigation"
              aria-controls="app-sidenav"
              aria-expanded={drawerOpen}
            >
              <i className="fa fa-bars" aria-hidden="true" />
            </button>
          )}

          <Link className="navbar-brand d-flex align-items-center" to="/">
            <i className="fa fa-shield-alt me-2" aria-hidden="true" />
            {APP_NAME}
          </Link>

          {/* Inline horizontal nav (desktop default). Hidden when compact;
              visually collapsed when it overflows (drawer takes over). */}
          {isAuthenticated && (
            <div
              ref={inlineNavRef}
              className={`top-nav-inline ${isCompact ? 'd-none' : 'd-flex'} ${
                navOverflow ? 'collapsed' : ''
              }`}
            >
              <NavMenu />
            </div>
          )}

          {/* Session / auth chrome — always visible in the bar */}
          <div className="d-flex align-items-center ms-auto">
            {/* Help — RBAC-aware in-app documentation (always available). */}
            <button
              type="button"
              className="btn btn-sm btn-outline-light rounded-circle d-inline-flex align-items-center justify-content-center me-2 px-0"
              style={{ width: '32px', height: '32px' }}
              onClick={() => setHelpOpen(true)}
              title="Help & Documentation"
              aria-label="Open help"
            >
              <i className="fa fa-question" aria-hidden="true" />
            </button>
            {isAuthenticated ? (
              <div className="dropdown">
                <a
                  href="#"
                  className="d-flex align-items-center text-white text-decoration-none dropdown-toggle"
                  id="accountMenu"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="User avatar" className="rounded-circle me-2" width="30" height="30" />
                  ) : (
                    // Initial-based avatar — avoids an external placeholder image
                    // (blocked by the production CSP img-src 'self' data:).
                    <span
                      className="rounded-circle me-2 d-inline-flex align-items-center justify-content-center bg-light text-primary fw-bold"
                      style={{ width: '30px', height: '30px', fontSize: '0.8rem' }}
                      aria-hidden="true"
                    >
                      {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="d-none d-sm-inline">{user?.name || user?.email || 'Account'}</span>
                </a>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                  <li>
                    <span className="dropdown-item-text small text-muted">{user?.email}</span>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" type="button" onClick={logout}>
                      <i className="fa fa-sign-out-alt me-2" aria-hidden="true" />
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <NavLink className="btn btn-light btn-sm" to="/login">
                <i className="fa fa-sign-in-alt me-1" aria-hidden="true" />
                Login
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Side drawer navigation (overflow / compact form of the nav) */}
      {isAuthenticated && (
        <div id="app-sidenav">
          <SideNav open={drawerOpen} onClose={closeDrawer} triggerRef={togglerRef} />
        </div>
      )}

      {/* main content */}
      <main className="layout-main">
        <Outlet />
      </main>

      {/* Mobile bottom tab bar (thumb-reachable primary nav) */}
      {showBottomNav && <BottomNav onMore={openDrawer} />}

      {/* RBAC-aware help drawer (opened from the ? button) */}
      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Footer */}
      <footer className="layout-footer text-center text-muted py-4 mt-5">
        <div className="container">
          <small>
            &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
          </small>
        </div>
      </footer>
    </div>
  );
}
